import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Trash2, Save, FileAudio2, Settings, ShoppingBag, ListMusic, DollarSign, Layers } from "lucide-react";

function BulkUpload({ onUploaded }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList).filter((f) => /\.(mp3|wav)$/i.test(f.name));
    if (!files.length) {
      toast.error("Drop MP3 or WAV files only.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const { data } = await api.post("/admin/beats/bulk", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success(`Uploaded ${data.count} beat${data.count === 1 ? "" : "s"} as drafts.`);
      onUploaded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [onUploaded]);

  return (
    <div
      className={`drop-zone rounded-md p-10 sm:p-14 text-center cursor-pointer ${dragOver ? "active" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => document.getElementById("bulk-file-input").click()}
      data-testid="bulk-upload-zone"
    >
      <input
        id="bulk-file-input"
        type="file"
        accept=".mp3,.wav,audio/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="bulk-file-input"
      />
      <UploadCloud className="w-12 h-12 mx-auto text-[var(--primary)]" />
      <div className="font-display text-xl font-bold mt-3 text-[var(--text)]">
        {uploading ? `Uploading… ${progress}%` : "Drop beats here"}
      </div>
      <p className="text-sm text-[var(--text-dim)] mt-1">
        MP3 or WAV · Multi-select supported · Becomes a draft you can edit & publish
      </p>
    </div>
  );
}

function BeatRow({ beat, onChange, onDelete }) {
  const [form, setForm] = useState(beat);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => setForm(beat), [beat]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/beats/${beat.id}`, {
        title: form.title,
        genre: form.genre,
        bpm: form.bpm ? Number(form.bpm) : null,
        key_signature: form.key_signature,
        price_mp3: Number(form.price_mp3),
        price_bundle: Number(form.price_bundle),
        tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags,
        description: form.description,
        license_terms: form.license_terms,
        cover_url: form.cover_url,
        is_published: !!form.is_published,
      });
      toast.success("Saved");
      onChange?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadAudio = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await api.post(`/admin/beats/${beat.id}/audio`, fd);
      toast.success(`${kind.toUpperCase()} attached`);
      onChange?.({
        ...beat,
        [`${kind}_path`]: "set",
        has_mp3: kind === "mp3" || beat.has_mp3,
        has_wav: kind === "wav" || beat.has_wav,
        has_stems: kind === "stems" || beat.has_stems,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="card-base p-5" data-testid={`admin-beat-row-${beat.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <input
          className="input-base md:col-span-3 px-3 py-2"
          placeholder="Title"
          value={form.title || ""}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid={`row-title-${beat.id}`}
        />
        <input
          className="input-base md:col-span-2 px-3 py-2"
          placeholder="Genre"
          value={form.genre || ""}
          onChange={(e) => setForm({ ...form, genre: e.target.value })}
          data-testid={`row-genre-${beat.id}`}
        />
        <input
          className="input-base md:col-span-1 px-3 py-2"
          placeholder="BPM"
          value={form.bpm || ""}
          onChange={(e) => setForm({ ...form, bpm: e.target.value })}
          data-testid={`row-bpm-${beat.id}`}
        />
        <input
          className="input-base md:col-span-1 px-3 py-2"
          placeholder="Key"
          value={form.key_signature || ""}
          onChange={(e) => setForm({ ...form, key_signature: e.target.value })}
          data-testid={`row-key-${beat.id}`}
        />
        <input
          className="input-base md:col-span-1 px-3 py-2"
          placeholder="$ MP3"
          type="number"
          step="0.01"
          value={form.price_mp3 ?? ""}
          onChange={(e) => setForm({ ...form, price_mp3: e.target.value })}
          data-testid={`row-price-mp3-${beat.id}`}
        />
        <input
          className="input-base md:col-span-1 px-3 py-2"
          placeholder="$ Bundle"
          type="number"
          step="0.01"
          value={form.price_bundle ?? ""}
          onChange={(e) => setForm({ ...form, price_bundle: e.target.value })}
          data-testid={`row-price-bundle-${beat.id}`}
        />
        <label className="md:col-span-1 flex items-center gap-2 text-xs text-[var(--text-dim)]">
          <input
            type="checkbox"
            checked={!!form.is_published}
            onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
            data-testid={`row-publish-${beat.id}`}
          />
          Live
        </label>
        <div className="md:col-span-2 flex items-center justify-end gap-2">
          <button onClick={save} disabled={saving} className="btn-primary px-3 py-2 text-sm flex items-center gap-1 disabled:opacity-60" data-testid={`row-save-${beat.id}`}>
            <Save className="w-4 h-4" /> {saving ? "…" : "Save"}
          </button>
          <button onClick={() => onDelete?.(beat)} className="px-2 py-2 text-[var(--text-dim)] hover:text-red-400" data-testid={`row-delete-${beat.id}`} aria-label="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <textarea
          className="input-base md:col-span-12 px-3 py-2 text-sm"
          placeholder="Tags (comma separated)"
          value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags || ""}
          onChange={(e) => setForm({ ...form, tags: e.target.value })}
          rows={1}
          data-testid={`row-tags-${beat.id}`}
        />

        <div className="md:col-span-12 flex flex-wrap gap-2 mt-1">
          <label className="btn-ghost text-xs px-3 py-1.5 cursor-pointer flex items-center gap-1.5">
            <FileAudio2 className="w-3.5 h-3.5" />
            {uploading === "preview" ? "Uploading…" : "Replace Preview"}
            <input type="file" accept=".mp3,.wav,audio/*" className="hidden" onChange={(e) => uploadAudio("preview", e.target.files?.[0])} data-testid={`row-upload-preview-${beat.id}`} />
          </label>
          <label className="btn-ghost text-xs px-3 py-1.5 cursor-pointer flex items-center gap-1.5">
            <FileAudio2 className="w-3.5 h-3.5" />
            {uploading === "mp3" ? "Uploading…" : beat.has_mp3 ? "Replace MP3" : "Attach MP3"}
            <input type="file" accept=".mp3,audio/mpeg" className="hidden" onChange={(e) => uploadAudio("mp3", e.target.files?.[0])} data-testid={`row-upload-mp3-${beat.id}`} />
          </label>
          <label className="btn-ghost text-xs px-3 py-1.5 cursor-pointer flex items-center gap-1.5">
            <FileAudio2 className="w-3.5 h-3.5" />
            {uploading === "wav" ? "Uploading…" : beat.has_wav ? "Replace WAV" : "Attach WAV"}
            <input type="file" accept=".wav,audio/wav" className="hidden" onChange={(e) => uploadAudio("wav", e.target.files?.[0])} data-testid={`row-upload-wav-${beat.id}`} />
          </label>
          <label className="btn-ghost text-xs px-3 py-1.5 cursor-pointer flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5" />
            {uploading === "stems" ? "Uploading…" : beat.has_stems ? "Replace Stems" : "Attach Stems (.zip)"}
            <input type="file" accept=".zip,.rar,.7z" className="hidden" onChange={(e) => uploadAudio("stems", e.target.files?.[0])} data-testid={`row-upload-stems-${beat.id}`} />
          </label>
          <span className="text-xs text-[var(--text-dim)] flex items-center gap-1.5 ml-auto font-mono-cartoon">
            MP3 {beat.has_mp3 ? "✓" : "—"} · WAV {beat.has_wav ? "✓" : "—"} · STEMS {beat.has_stems ? "✓" : "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [form, setForm] = useState({ paypal_client_id: "", paypal_secret: "", paypal_mode: "sandbox", resend_api_key: "", sender_email: "" });
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      setStatus(data);
      setForm((f) => ({ ...f, paypal_client_id: data.paypal_client_id || "", paypal_mode: data.paypal_mode || "sandbox", sender_email: data.sender_email || "" }));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== "" && v != null) payload[k] = v; });
      await api.post("/admin/settings", payload);
      const { data } = await api.get("/admin/settings");
      setStatus(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-base p-6 mt-6" data-testid="settings-panel">
      <div className="flex items-center gap-2 mb-4">
        <Settings className="w-5 h-5 text-[var(--secondary)]" />
        <h3 className="font-display text-xl font-bold">Payments &amp; Email</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--text-dim)]">PayPal Mode</label>
          <select className="input-base w-full mt-1 px-3 py-2" value={form.paypal_mode} onChange={(e) => setForm({ ...form, paypal_mode: e.target.value })} data-testid="settings-paypal-mode">
            <option value="sandbox">Sandbox</option>
            <option value="live">Live</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--text-dim)]">PayPal Client ID</label>
          <input className="input-base w-full mt-1 px-3 py-2" value={form.paypal_client_id} onChange={(e) => setForm({ ...form, paypal_client_id: e.target.value })} data-testid="settings-paypal-client-id" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--text-dim)]">PayPal Secret {status?.paypal_secret_set && <span className="text-[var(--secondary)] ml-1">(set)</span>}</label>
          <input className="input-base w-full mt-1 px-3 py-2" type="password" placeholder={status?.paypal_secret_set ? "•••• (leave blank to keep)" : ""} value={form.paypal_secret} onChange={(e) => setForm({ ...form, paypal_secret: e.target.value })} data-testid="settings-paypal-secret" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--text-dim)]">Sender Email</label>
          <input className="input-base w-full mt-1 px-3 py-2" value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} placeholder="onboarding@resend.dev" data-testid="settings-sender-email" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider text-[var(--text-dim)]">Resend API Key {status?.resend_api_key_set && <span className="text-[var(--secondary)] ml-1">(set)</span>}</label>
          <input className="input-base w-full mt-1 px-3 py-2" type="password" placeholder={status?.resend_api_key_set ? "•••• (leave blank to keep)" : "re_..."} value={form.resend_api_key} onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })} data-testid="settings-resend-key" />
        </div>
      </div>
      <p className="text-xs text-[var(--text-dim)] mt-4 leading-relaxed">
        Need PayPal keys? Sign in at developer.paypal.com → Apps & Credentials → create a REST app → copy the Client ID & Secret. Start in <b>Sandbox</b>, switch to <b>Live</b> when ready.
      </p>
      <button onClick={save} disabled={saving} className="btn-primary mt-5 px-5 py-2 flex items-center gap-2 disabled:opacity-60" data-testid="settings-save-btn">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}

function StatsBar({ stats }) {
  const items = [
    { label: "Total sales", value: `$${(stats?.total_sales || 0).toFixed(2)}`, icon: DollarSign },
    { label: "Orders", value: stats?.total_orders ?? 0, icon: ShoppingBag },
    { label: "Beats", value: stats?.total_beats ?? 0, icon: ListMusic },
    { label: "Published", value: stats?.published_beats ?? 0, icon: ListMusic },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
      {items.map((it) => (
        <div key={it.label} className="card-base p-4">
          <div className="flex items-center gap-2 text-[var(--text-dim)] text-xs uppercase tracking-wider">
            <it.icon className="w-3.5 h-3.5" /> {it.label}
          </div>
          <div className="font-display text-2xl font-bold mt-2 text-[var(--secondary)]">{it.value}</div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [beats, setBeats] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);

  const refresh = useCallback(async () => {
    const [b, o, s] = await Promise.all([
      api.get("/beats?include_unpublished=true"),
      api.get("/admin/orders"),
      api.get("/admin/stats"),
    ]);
    setBeats(b.data);
    setOrders(o.data);
    setStats(s.data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onDelete = async (beat) => {
    if (!window.confirm(`Delete "${beat.title}"?`)) return;
    try {
      await api.delete(`/admin/beats/${beat.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 pb-40 fade-up" data-testid="admin-dashboard-page">
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter">Studio</h1>
      <p className="text-sm text-[var(--text-dim)] mt-1">Drop beats. Set prices. Get paid.</p>

      <div className="mt-8"><StatsBar stats={stats} /></div>

      <div className="mt-8"><BulkUpload onUploaded={refresh} /></div>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><ListMusic className="w-5 h-5" /> Beats</h2>
        {beats.length === 0 ? (
          <div className="card-base p-10 text-center text-[var(--text-dim)]" data-testid="admin-beats-empty">
            No beats yet. Drop files above to get started.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-beats-list">
            {beats.map((b) => (
              <BeatRow key={b.id} beat={b} onChange={refresh} onDelete={onDelete} />
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Recent sales</h2>
        {orders.length === 0 ? (
          <div className="card-base p-10 text-center text-[var(--text-dim)]" data-testid="admin-orders-empty">No orders yet.</div>
        ) : (
          <div className="card-base overflow-x-auto" data-testid="admin-orders-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--text-dim)] uppercase text-xs tracking-wider">
                  <th className="p-4">Date</th>
                  <th className="p-4">Beat</th>
                  <th className="p-4">License</th>
                  <th className="p-4">Customer</th>
                  <th className="p-4">Amount</th>
                  <th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.paypal_order_id} className="border-t border-[var(--border)]">
                    <td className="p-4 text-[var(--text-dim)]">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="p-4">{o.beat_title}</td>
                    <td className="p-4 uppercase text-xs">{o.license_type}</td>
                    <td className="p-4">{o.customer_email}</td>
                    <td className="p-4 text-[var(--secondary)] font-bold">${o.amount?.toFixed(2)}</td>
                    <td className="p-4">{o.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-2">
        <SettingsPanel />
      </section>
    </div>
  );
}
